import { GetStaticProps, GetStaticPropsContext } from 'next'
import { Trans, useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'

import ROUTES from '~/shared/constants/routes.const'
import ShowLoginForm from '~/auth/components/ShowLoginForm'
import AsideContent from '~/components/Game/GameLogIn'
import GuestGuard from '~/components/GuestGuard'
import { COMMON_TRANSLATION_MODULE_NAMES, DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES } from '~/shared/layouts/constants'
import { getServerlessImageHandlerUrl } from '~/components/Image/helpers'

import GamesSchema from '~/components/Metadata/Schema/GameSchema'
import { GameDetails } from '~/components/Metadata/Metadata.types'
import Metatags from '~/components/Metadata/Metatags'
import { Breakpoint, useCheckBreakpoint } from '~/utils'
import { Footer, Link } from '~/components'
import ResponsiveImage from '~/components/Image/ResponsiveImage'

const StacknLoadLogin = (): React.ReactElement => {
  const { t } = useTranslation('games')
  const isSmallScreen = useCheckBreakpoint({ lt: Breakpoint.LG })
  const imageHeight = isSmallScreen ? 31 : 68
  const imageWidth = isSmallScreen ? 160 : 355

  const stacknLoadLogo = getServerlessImageHandlerUrl({
    key: 'logos/stackn-load-logo.png',
    edits: {
      resize: {
        fit: 'contain',
      },
    },
  })

  const gameDetails: GameDetails = {
    name: t('cranegame.gameTitle'),
    image: stacknLoadLogo,
    description: t('cranegame.schemaDescription'),
    genre: ['Puzzle', '3D', 'Crane'],
    playMode: ['SinglePlayer'],
    typicalAgeRange: '10+',
    url: ROUTES.STACKNLOAD_LOGIN,
  }

  return (
    <div className="relative flex flex-col items-center bg-gradient-tod-heading w-full min-h-screen mx-auto overflow-x-hidden lg:pt-5 xl:pt-0">
      <Metatags
        image={{ srcKey: 'meta-images/stackn-load-ogp.png' }}
        metaDescription={t('cranegame.metaDescription')}
        noindex
        title={t('cranegame.metaTitle')}
      />
      <GamesSchema gameDetails={gameDetails} />
      <GuestGuard redirect={ROUTES.STACKNLOAD}>
        <div className="min-h-screen flex flex-col justify-center items-center">
          <div className="h-20 flex flex-col items-center justify-center">
            <Link to={ROUTES.HOME}>
              <ResponsiveImage
                height={imageHeight}
                loading="eager"
                srcKey="logos/house-of-math-logo-white.png"
                width={imageWidth}
              />
            </Link>
          </div>
          <div className="flex flex-col lg:flex-row lg:mx-8 xl:mx-20 lg:mt-10 xl:mt-20">
            <AsideContent
              gameLogoAlt={t('cranegame.gameTitle')}
              gameLogoSrc="gamification/stack-n-load/StacknLoad_Logo_Nyversjon3.png"
              heading={t('cranegame.gameTitle')}
              ingress={Trans({
                t,
                i18nKey: 'cranegame.logInIngress',
                components: { strong: <span className="font-bold" /> },
                children: t('cranegame.logInIngress'),
              })}
            />
            <div className="flex justify-center bg-white rounded-7 px-2 md:px-6 py-3 m-4">
              <ShowLoginForm />
            </div>
          </div>
        </div>
        <div className="w-full mt-auto">
          <Footer />
        </div>
      </GuestGuard>
    </div>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }: GetStaticPropsContext) => ({
  props: {
    ...(await serverSideTranslations(locale as string, [
      ...DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES,
      ...COMMON_TRANSLATION_MODULE_NAMES,
      'welcome',
      'logIn',
      'multgame',
      'games',
      'authenticate',
      'fields',
    ])),
  },
})

export default StacknLoadLogin
